import { type JSX, useEffect, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAssessmentSubjects } from 'Api/UcrmTprm/UcrmTprmApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { iso8601ToJsDate, iso8601ToUsDateLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { numberAsEffectivenessString } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { numberAsRiskRatingString } from 'Models/RiskRating';
import { AssociatedThirdPartyService, ThirdPartyContact, ThirdPartyService } from 'Models/TPRM';
import { UseCase } from 'Models/Ucrm';

/**
 * These attributes are nested. MUI Data Grid doesn't allow directly accessing multiple nested attributes from the same dictionary/map (that I know of), hence this hacky interface combined with valueGetter calls below.
 * @param associated_tprm_services Only present on UCRM use cases.
 */
interface AssessmentSubjectExportRow extends ThirdPartyService {
    associated_tprm_services: AssociatedThirdPartyService[];
    state: AssessmentWorkflowState;
    irq_inherent_risk_score: number;
    final_review_control_effectiveness: number;
    final_review_residual_risk_score: number;
}

/** Columns that are omitted from the UCRM export, since use cases do not have these fields. */
const TPRM_ONLY_FIELDS: (keyof AssessmentSubjectExportRow)[] = ['responsible_organization', 'vendor_contacts'];

/** Columns that are omitted from the TPRM export, since third-party services do not have these fields. */
const UCRM_ONLY_FIELDS: (keyof AssessmentSubjectExportRow)[] = ['associated_tprm_services'];

export interface AssessmentSubjectsExportProps {
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;

    disableVirtualization?: boolean;
}

/** Exports TPRM third-party services or UCRM use cases, depending on `module`. */
export const AssessmentSubjectsExport = (props: AssessmentSubjectsExportProps): JSX.Element => {
    const [assessmentSubjects, setAssessmentSubjects] = useState<(ThirdPartyService | UseCase)[]>();
    const [error, setError] = useState<string>();
    const cachedData = useCachedData();

    // Save the Tags map locally so it can be repeatedly accessed rather than recreating it as the rows and Tags are looped through.
    const tagsMap = cachedData.getTagsMap();

    useEffect(() => {
        const fetchAssessmentSubjects = async (): Promise<void> => {
            try {
                const assessmentSubjectsResponse = await getAssessmentSubjects(props.moduleApi);
                setAssessmentSubjects(assessmentSubjectsResponse.data);
            } catch (error) {
                setError(error.message);
            }
        };

        fetchAssessmentSubjects();
    }, [props.moduleApi]);

    const isUcrm = props.module === ProductModule.UCRM;
    const organizationTerm = isUcrm ? 'Business' : 'Third-Party';
    const subjectTerm = isUcrm ? 'Use Case' : 'Third-Party Service';
    const pluralSubjectTerm = isUcrm ? 'Use Cases' : 'Third-Party Services';

    const allColumns: GridColumn<AssessmentSubjectExportRow>[] = [
        { field: 'vendor_name', headerName: `${organizationTerm} Name`, width: 300 },
        { field: 'name', headerName: isUcrm ? 'Use Case Name' : 'Service Name', width: 300 },
        { field: 'description', headerName: 'Description', width: 300 },
        { field: 'state', headerName: 'Assessment State', width: 300, valueGetter: (_value, row) => row.assessment_workflow_data.state },
        { field: 'assessment_workflow_setup', headerName: 'Due Diligence Workflow Due Date', width: 300, valueGetter: (value) => iso8601ToJsDate(value.due_date), valueFormatter: (value) => iso8601ToUsDateLong(value) },
        { field: 'irq_inherent_risk_score', headerName: 'Inherent Risk Score', width: 300, valueGetter: (_value, row) => numberAsRiskRatingString(row.assessment_workflow_data.irq_inherent_risk_score ?? 0) },
        { field: 'final_review_control_effectiveness', headerName: 'Overall Control Effectiveness', width: 300, valueGetter: (_value, row) => numberAsEffectivenessString(row.assessment_workflow_data.final_review_human_assessment.effectiveness ?? 0) },
        { field: 'final_review_residual_risk_score', headerName: 'Residual Risk Score', width: 300, valueGetter: (_value, row) => numberAsRiskRatingString(row.assessment_workflow_data.final_review_human_assessment.residual_risk ?? 0) },
        { field: 'vendor_service_manager_user_id', headerName: `${subjectTerm} Manager`, width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        { field: 'responsible_organization', headerName: 'Responsible Organization', width: 300 },
        {
            field: 'delegates',
            headerName: 'Delegates',
            width: 300,
            valueGetter: (value) =>
                value
                    .map((userId: string) => getUserNameFromSubject(userId, cachedData.users))
                    .sort()
                    .join(', '),
        },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 300,
            valueGetter: (_value, row) =>
                row.tags
                    .sort()
                    .map((tagId: string) => tagsMap.get(tagId)?.title ?? tagId)
                    .join(', '),
        },
        {
            field: 'vendor_contacts',
            headerName: 'Third-Party Service Contacts',
            width: 300,
            valueGetter: (value) =>
                value
                    // Only show the name for each contact. Omit their email address and phone number.
                    .filter((thirdPartyContact: ThirdPartyContact) => thirdPartyContact.name !== undefined)
                    .sort((thirdPartyContactA: ThirdPartyContact, thirdPartyContactB: ThirdPartyContact) => (thirdPartyContactA?.name || '').localeCompare(thirdPartyContactB?.name || ''))
                    .map((thirdPartyContact: ThirdPartyContact) => thirdPartyContact.name)
                    .join(', '),
        },
        {
            field: 'associated_tprm_services',
            headerName: 'Third-Party Services',
            width: 300,
            valueGetter: (value) =>
                value
                    .map((service: AssociatedThirdPartyService) => `${service.vendor_name} - ${service.name}`)
                    .sort()
                    .join(', '),
        },
        dateColumn('created_time', 'Created Date'),
        { field: 'created_by', headerName: 'Created By', width: 300, valueGetter: (value) => (value === undefined ? undefined : getUserNameFromSubject(value, cachedData.users)) },
        dateColumn('finalized_time', 'Finalized Date'),
        { field: 'finalized_by', headerName: 'Finalized By', width: 300, valueGetter: (value) => (value === undefined ? undefined : getUserNameFromSubject(value, cachedData.users)) },
        { field: 'finalization_state', headerName: 'Finalization State', width: 300 },
        { field: 'active_state', headerName: 'Active State', width: 300 },
        { field: 'id', headerName: 'ID', width: 300 },
    ];
    const omittedFields = isUcrm ? TPRM_ONLY_FIELDS : UCRM_ONLY_FIELDS;
    const columns = allColumns.filter((column) => !omittedFields.includes(column.field as keyof AssessmentSubjectExportRow));

    if (assessmentSubjects) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>{pluralSubjectTerm}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={`${pluralSubjectTerm} Export`}
                body={[{ content: <DataGrid columns={columns} getRowId={(row: AssessmentSubjectExportRow) => row.id} rows={assessmentSubjects} title={`${pluralSubjectTerm} Export Preview`} fileName={pluralSubjectTerm} disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else return <Placeholder />;
};
